import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CardAttributeUpdateActionsEnum} from '../../../../helpers/models/cardAttributeUpdateHelpers';
import {CardNoteUpdateActionsEnum} from '../../../../helpers/models/cardNoteHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {ContactDetailsUndoBannerState} from '../../../../store/panels/app/banners/bannersState';
import {ContactDetailsUndoBannerAction} from '../../../../store/panels/app/banners/contactDetailsUndoBannersActions';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerAction} from '../../../common/banners/bannerAction';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {BannerText} from '../../../common/banners/bannerText';
import {ContactSourceDisplayName} from '../../../contacts/contactSourceDisplayName';

/*
 * Constants.
 */

enum ContactDetailsUndoBannerVerbsEnum {
  ADD = 'add',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

/*
 * Props.
 */

interface ContactDetailsUndoBannerProps {
  state: ContactDetailsUndoBannerState;
  onUndoClick: Handler;

  account?: AccountModel;
}

interface BannerAdditionalDetails {
  account?: AccountModel;
}

/*
 * Component.
 */

export const ContactDetailsUndoBanner: FC<ContactDetailsUndoBannerProps> = (props) => {
  const {
    state: {uid, action},
    onUndoClick,
    account,
  } = props;

  return (
    <Banner
      bannerType={BannerTypesEnum.INLINE}
      priority={BannerPrioritiesEnum.TRANSIENT}
      interactionId={`contact-details-undo-banner-${uid}`}
    >
      <BannerIcon iconName="contactsFilledSmall" />
      <BannerText>{getBannerText(action as ContactDetailsUndoBannerAction, {account})}</BannerText>
      <BannerAction onClick={onUndoClick} interactionId="contact-details-undo-banner-undo-button">
        <Trans id="banner-undo">Undo</Trans>
      </BannerAction>
    </Banner>
  );
};

/*
 * Helpers.
 */

function getBannerText(action: ContactDetailsUndoBannerAction, additionalDetails: BannerAdditionalDetails) {
  const subject = getSubject(action);
  const verb = getVerb(action);
  const additionalText = getAdditionalText(action, additionalDetails);

  return (
    <>
      {subject} {verb} {additionalText}
    </>
  );
}

function getSubject(action: ContactDetailsUndoBannerAction) {
  switch (action.type) {
    case CardAttributeUpdateActionsEnum.UPDATE_NAME:
      return <Trans id="contact-details-undo-banner-name">Name</Trans>;

    case CardAttributeUpdateActionsEnum.ADD_DESCRIPTION:
    case CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
    case CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return <Trans id="contact-details-undo-banner-description">Description</Trans>;

    case CardAttributeUpdateActionsEnum.ADD_LINK:
    case CardAttributeUpdateActionsEnum.UPDATE_LINK:
    case CardAttributeUpdateActionsEnum.DELETE_LINK:
      return <Trans id="contact-details-undo-banner-link">Link</Trans>;

    case CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
    case CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
    case CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return action.payload.customField.name;

    case CardAttributeUpdateActionsEnum.ADD_CONTACT:
    case CardAttributeUpdateActionsEnum.UPDATE_CONTACT:
    case CardAttributeUpdateActionsEnum.DELETE_CONTACT:
      return <ContactSourceDisplayName contactUid={action.payload.contactUid} shouldCapitalize />;

    case CardNoteUpdateActionsEnum.CREATE_NOTE:
    case CardNoteUpdateActionsEnum.UPDATE_NOTE:
    case CardNoteUpdateActionsEnum.DELETE_NOTE:
      return <Trans id="contact-details-undo-banner-note">Note</Trans>;

    case CardAttributeUpdateActionsEnum.ADD_ACCOUNT:
      return <Trans id="contact-details-undo-banner-account-contact">Contact</Trans>;
    case CardAttributeUpdateActionsEnum.UPDATE_ACCOUNT:
    case CardAttributeUpdateActionsEnum.DELETE_ACCOUNT:
      return <Trans id="contact-details-undo-banner-account">Account</Trans>;

    default:
      return '';
  }
}

function getVerb(action: ContactDetailsUndoBannerAction) {
  const actionTypeString = action.type.toString().toLowerCase();

  if (
    actionTypeString.includes(ContactDetailsUndoBannerVerbsEnum.ADD) ||
    actionTypeString.includes(ContactDetailsUndoBannerVerbsEnum.CREATE)
  ) {
    return <Trans id="contact-details-undo-banner-added">added</Trans>;
  }

  if (actionTypeString.includes(ContactDetailsUndoBannerVerbsEnum.UPDATE)) {
    return <Trans id="contact-details-undo-banner-updated">updated</Trans>;
  }

  if (actionTypeString.includes(ContactDetailsUndoBannerVerbsEnum.DELETE)) {
    return <Trans id="contact-details-undo-banner-removed">removed</Trans>;
  }

  return '';
}

function getAdditionalText(
  action: ContactDetailsUndoBannerAction,
  additionalDetails: BannerAdditionalDetails,
) {
  const {account} = additionalDetails;

  if (action.type === CardAttributeUpdateActionsEnum.ADD_ACCOUNT && account) {
    return <Trans id="contact-details-undo-banner-account-to">to {account.name}</Trans>;
  }

  return '';
}
